import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { deleteCoupon } from '../../actions/certificationActions'
import { showGenericError } from '../../utility/certificationBuilder'
import CouponFormModal from './CouponFormModal'
import DeleteConfirmationModal from './DeleteConfirmationModal'

import classes from './CouponsList.module.scss'

const CouponsList = ({ certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [formCoupon, setFormCoupon] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [deletingCoupon, setDeletingCoupon] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const openForm = (coupon = null) => { setFormCoupon(coupon); setShowForm(true) }

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deleteCoupon({ certificationId: certification.id, id: deletingCoupon.id })).unwrap()
      .then(() => setDeletingCoupon(null))
      .catch(showGenericError)
      .finally(() => setIsDeleting(false))
  }

  const description = (coupon) => [
    coupon.label,
    t('certificationBuilder.coupons.percentOff', { percent: coupon.percentOff }),
    coupon.expiresAt && t('certificationBuilder.coupons.until', { date: moment(coupon.expiresAt).format('ll') }),
    coupon.organization && t('certificationBuilder.coupons.organizationOnly', { name: coupon.organization.name }),
  ].filter(Boolean).join(' · ')

  const usesLabel = (coupon) => coupon.maxUses
    ? t('certificationBuilder.coupons.maxUses', { count: coupon.maxUses })
    : t('certificationBuilder.coupons.unlimitedUses')

  return (
    <div>
      <div className='d-flex align-items-baseline justify-content-between mb-2'>
        <span className={classes.label}>{t('certificationBuilder.coupons.title')}</span>
        <button type='button' className={classes.linkButton} onClick={() => openForm()}>
          <i className='fa fa-plus mr-1' aria-hidden='true' />
          {t('certificationBuilder.coupons.new')}
        </button>
      </div>

      {certification.coupons.length === 0 && <p className={classes.empty}>{t('certificationBuilder.coupons.empty')}</p>}

      <div className='d-flex flex-column'>
        {certification.coupons.map(coupon => (
          <div key={coupon.id} className={classes.row}>
            <span className={classes.code}>{coupon.code}</span>
            <span className={classes.description}>{description(coupon)}</span>
            <span className={classes.uses}>{usesLabel(coupon)}</span>
            <span className={classNames(classes.status, { [classes.statusActive]: coupon.active })}>
              {t(coupon.active ? 'certificationBuilder.coupons.active' : 'certificationBuilder.coupons.inactive')}
            </span>
            <span className='d-flex align-items-center justify-content-end'>
              <button type='button' className={classes.editButton} onClick={() => openForm(coupon)}>{t('common.edit')}</button>
              <button type='button' className={classes.iconButton} title={t('common.delete')} onClick={() => setDeletingCoupon(coupon)}>
                <i className='fa fa-trash-o' aria-hidden='true' />
              </button>
            </span>
          </div>
        ))}
      </div>

      {showForm && (
        <CouponFormModal isOpen={showForm} certificationId={certification.id} coupon={formCoupon} onClose={() => setShowForm(false)} />
      )}

      <DeleteConfirmationModal
        show={!!deletingCoupon}
        title={t('certificationBuilder.coupons.deleteTitle', { code: deletingCoupon?.code })}
        message={t('certificationBuilder.coupons.deleteMessage')}
        isLoading={isDeleting}
        onClose={() => setDeletingCoupon(null)}
        onProceed={handleDelete}
      />
    </div>
  )
}

CouponsList.propTypes = {
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    coupons: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      code: PropTypes.string.isRequired,
      label: PropTypes.string,
      percentOff: PropTypes.number.isRequired,
      maxUses: PropTypes.number,
      expiresAt: PropTypes.string,
      active: PropTypes.bool.isRequired,
      organization: PropTypes.shape({ name: PropTypes.string.isRequired }),
    })).isRequired,
  }).isRequired,
}

export default CouponsList
